import { useEffect, useRef, useCallback } from 'react'

/**
 * useFontLoadDetector - Custom hook that detects when a font has loaded by comparing 
 * the heights of two elements with the same custom font but different fallback fonts.
 *
 * @param {string} fontFamily - The font family to test (e.g., 'MyFont-Regular')
 * @param {number} fontSize - Font size to use for testing (default: 100)
 * @param {function} onLoad - Callback fired when font loads successfully
 * @param {function} onError - Callback fired when font fails to load after timeout
 * @param {string} testText - Text to use for testing (default: 'A')
 * @param {number} timeout - Time in ms before firing onError (default: 1000)
 * @returns {object} - Object with refs for the test elements
 */
export default function useFontLoadDetector({
  fontFamily,
  fontSize = 100,
  onLoad,
  onError,
  testText = 'A',
  timeout = 1000
}) {
  const primaryRef = useRef(null)
  const confirmationRef = useRef(null)
  const callbackFiredRef = useRef(false)

  // Memoize callbacks to prevent unnecessary re-runs
  const stableOnLoad = useCallback(onLoad, [])
  const stableOnError = useCallback(onError, [])

  useEffect(() => {
    if (!fontFamily) return

    // Reset for new font
    callbackFiredRef.current = false

    let intervalId = null
    let timeoutId = null

    const checkFontLoad = () => {
      if (!primaryRef.current || !confirmationRef.current || callbackFiredRef.current) return

      const primaryHeight = primaryRef.current.offsetHeight
      const confirmationHeight = confirmationRef.current.offsetHeight

      if (primaryHeight === confirmationHeight && primaryHeight > 0) {
        // Heights match and are valid - font has loaded successfully
        callbackFiredRef.current = true
        if (intervalId) clearInterval(intervalId)
        if (timeoutId) clearTimeout(timeoutId)

        if (stableOnLoad) {
          stableOnLoad(primaryHeight, fontSize)
        }
      }
    }

    // Start checking after a brief delay to ensure DOM is ready
    const startChecking = () => {
      checkFontLoad()

      if (!callbackFiredRef.current) {
        intervalId = setInterval(checkFontLoad, 50)

        timeoutId = setTimeout(() => {
          if (!callbackFiredRef.current) {
            callbackFiredRef.current = true
            if (intervalId) clearInterval(intervalId)

            if (stableOnError) {
              stableOnError('Font not loading')
            }
          }
        }, timeout)
      }
    }

    const initialDelay = setTimeout(startChecking, 10)

    return () => {
      clearTimeout(initialDelay)
      if (intervalId) clearInterval(intervalId)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [fontFamily, fontSize, timeout, stableOnLoad, stableOnError])

  return {
    primaryRef,
    confirmationRef,
    testElements: fontFamily ? (
      <div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}>
        <div
          ref={primaryRef}
          style={{
            lineHeight: 'normal',
            fontFamily: `${fontFamily}, sans-serif`,
            fontSize: fontSize
          }}
        >
          {testText}
        </div>
        <div
          ref={confirmationRef}
          style={{
            lineHeight: 'normal',
            fontFamily: `${fontFamily}, cursive`,
            fontSize: fontSize
          }}
        >
          {testText}
        </div>
      </div>
    ) : null
  }
}